import SortOrders from "./sort-orders";
import ServiceUtils from "./service-utils";
import Parameter from "./parameter";
import Feedback from "./feedback";
import Api from "./api";
import { sortByOrder } from "../utils";
import ISmsTemplate from "../interface/sms-template";
import IApiEntity from "../interface/api/api-entity";
import ServiceName from "../enum/service-name";
import ParamStorage from "../enum/param-storage";
import ErrorCode from "../enum/error-code";
import ParameterStorage from "../class/parameter-storage";

class SmsTemplateInstance {

    private readonly Store = ParamStorage.SmsTpl;

    /* Public Methods */

    public getAll(entityId: number | null = null): ISmsTemplate[] {
        const all: ISmsTemplate[] = this.getStore().getAll();
        if (entityId) {
            const order: number[] = SortOrders.get(this.Store, entityId);
            const result: ISmsTemplate[] = all.filter((tpl: ISmsTemplate) => tpl.entityId === entityId);
            return sortByOrder(result, 'id', order);
        }
        return all;
    }

    public get(id: number): ISmsTemplate | null {
        return this.getStore().get(id);
    }

    public async load(id: number, raw: boolean = false): Promise<ISmsTemplate | null> {
        const tpl: ISmsTemplate | null = await this.getStore().load(id);
        if (tpl && tpl.body && !raw) {
            tpl.body = ServiceUtils.replaceSubsToName(tpl.entityId, tpl.body);
        }
        return tpl;
    }

    public async create(tpl: ISmsTemplate): Promise<ISmsTemplate> {
        if (this.valid(tpl)) {
            return await this.getStore().create(this.toRaw(tpl));
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async update(tpl: ISmsTemplate): Promise<ISmsTemplate> {
        if (this.valid(tpl)) {
            return await this.getStore().update(this.toRaw(tpl));
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number): Promise<number> {
        const tpl: ISmsTemplate | null = this.get(id);
        if (tpl) {
            await Feedback.renameKeysToFixed(ServiceName.Sms, tpl);
            return await this.getStore().delete(id);
        }
        return id;
    }

    public valid(tpl: ISmsTemplate): boolean {
        const entity: IApiEntity | null = Api.getEntity(tpl.entityId);
        let valid: boolean = !!entity;
        valid = valid && ServiceUtils.validPath(tpl.emissionPath);
        valid = valid && !!tpl.name;
        valid = valid && !!tpl.sender;
        valid = valid && !!tpl.body;
        return valid;
    }

    public async prepareToSend(tpl: ISmsTemplate, save: boolean): Promise<ISmsTemplate> {
        if (save) {
            let result: ISmsTemplate;
            if (tpl.id) {
                result = await this.update(tpl);
            } else {
                result = await this.create(tpl);
            }
            return (await this.load(result.id as number, true)) as ISmsTemplate;
        } else {
            return this.toRaw(tpl);
        }
    }

    public async setOrder(entityId: number | null = null, order: number[]): Promise<ISmsTemplate[]> {
        await SortOrders.set({ param: this.Store, entityId, order });
        return this.getAll(entityId);
    }

    /* Private Methods */

    public getByAllEntities(): ISmsTemplate[] {
        return this.getStore().getAll();
    }

    private getStore(): ParameterStorage<ISmsTemplate> {
        return Parameter.get(this.Store);
    }

    private toRaw(tpl: ISmsTemplate): ISmsTemplate {
        tpl.body = ServiceUtils.replaceSubsToId(tpl.entityId, tpl.body || '');
        /* Trimming all string data */
        tpl.name = tpl.name.trim();
        tpl.sender = tpl.sender.trim();
        tpl.body = tpl.body.trim();
        return tpl;
    }

}

const SmsTemplate = new SmsTemplateInstance();

export default SmsTemplate;